import { percent } from '@affine/core/utils';
import { DebugLogger } from '@affine/debug';
import { FeatureName, type WorkspaceQuotaQuery } from '@affine/graphql';
import { isBackendError, isNetworkError } from '@docnboard/rest';
import { cssVar } from '@docnboard/theme';
import type { DocsService, WorkspaceService } from '@toeverything/infra';
import {
  backoffRetry,
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  mapInto,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { combineLatest, exhaustMap, map } from 'rxjs';

import type { WorkspaceQuotaBlobStore } from '../stores/blob';
import type { WorkspaceQuotaStore } from '../stores/quota';

type QuotaType = WorkspaceQuotaQuery['getWorkspace']['quota'];

// DNB_NEW_NAMES_FIX
const logger = new DebugLogger('dnb:workspace-quota');

const color = (percent: number) =>
  percent > 80 ? cssVar('errorColor') : cssVar('processingColor');

export class WorkspaceQuota extends Entity {
  quota$ = new LiveData<QuotaType | null>(null);
  isLoading$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  docLimitCalc = (value: number | null | undefined) => {
    if (value === null) return Infinity;
    if (value === undefined) return 0;
    return value;
  };

  max$ = LiveData.from(
    this.quota$.pipe(
      map(quota => ({
        docLimit:
          quota?.name === FeatureName.unlimited
            ? Infinity
            : this.docLimitCalc(quota?.docLimit),
      }))
    ),
    { docLimit: 0 }
  );

  maxFormatted$ = this.max$.map(max =>
    max
      ? { docLimit: max.docLimit === Infinity ? '∞' : max.docLimit.toString() }
      : null
  );

  used$ = LiveData.from(
    combineLatest([
      this.docsService.list.docs$,
      this.docsService.list.trashDocs$,
    ]).pipe(
      map(([docs]) => {
        return { usedDocCount: docs.filter(doc => !doc.trash$.value).length };
      })
    ),
    { usedDocCount: 0 }
  );

  percent$ = LiveData.computed(get => {
    const max = get(this.max$);
    const used = get(this.used$);
    if (max === null || used === null) {
      return null;
    }
    return {
      docQuota:
        max.docLimit === Infinity
          ? 0
          : percent(used.usedDocCount, max.docLimit),
    };
  });

  color$ = this.percent$.map(percent =>
    percent !== null ? { docQuota: color(percent.docQuota) } : null
  );

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: WorkspaceQuotaStore,
    private readonly docsService: DocsService,
    private readonly storeQuotaBlob: WorkspaceQuotaBlobStore
  ) {
    super();
  }

  revalidate = effect(
    exhaustMap(() => {
      return fromPromise(signal =>
        this.store.fetchWorkspaceQuota(
          this.workspaceService.workspace.id,
          signal
        )
      ).pipe(
        backoffRetry({ when: isNetworkError, count: Infinity }),
        backoffRetry({ when: isBackendError, count: 3 }),
        mapInto(this.quota$),
        catchErrorInto(this.error$, error => {
          logger.error('Failed to fetch isOwner', error);
        }),
        onStart(() => this.isLoading$.setValue(true)),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  getQuotaBlob() {
    return this.storeQuotaBlob.fetchWorkspaceQuotaBlob(
      this.workspaceService.workspace.id
    );
  }
}
